import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import {
  ArrowLeft,
  Save,
  FileText,
  Plus,
  X,
  Target,
  BookOpen
} from "lucide-react";
import { getMe, getAdminLesson, saveAdminLesson } from "../../api/client";
import { useTranslation } from "../../hooks/useTranslation";
import ContentSectionEditor from "../../components/teacher/ContentSectionEditor";

export default function LessonCreator() {
  const { t } = useTranslation();
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const isNew = lessonId === "new";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  const [form, setForm] = useState({
    id: "",
    title: "",
    description: "",
    category: "grundlagen",
    difficulty: "beginner",
    duration_minutes: 30,
    points_reward: 100,
    order_index: 1,
    learning_objectives: [],
    content_sections: []
  });

  const [newObjective, setNewObjective] = useState("");
  const [validation, setValidation] = useState({});

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, [moduleId, lessonId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // TESTING MODE: Skip auth check
      const me = await getMe().catch(() => null);
      // Allow access without login for testing

      if (!isNew) {
        const data = await getAdminLesson(moduleId, lessonId);
        if (data && data.lesson) {
          setForm({
            id: data.lesson.id || lessonId,
            title: data.lesson.title || "",
            description: data.lesson.description || "",
            category: data.lesson.category || "grundlagen",
            difficulty: data.lesson.difficulty || "beginner",
            duration_minutes: data.lesson.duration_minutes || 30,
            points_reward: data.lesson.points_reward || 100,
            order_index: data.lesson.order_index || 1,
            learning_objectives: data.lesson.learning_objectives || [],
            content_sections: data.lesson.content_sections || []
          });
        }
      }
    } catch (err) {
      console.error("LessonCreator load error:", err);
      if (!isNew) {
        setError(t.contentCreator?.lessonNotFound || "Lektion nicht gefunden");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.id.trim()) {
      errors.id = t.contentCreator?.idRequired || "ID ist erforderlich";
    }

    if (!form.title.trim()) {
      errors.title = t.contentCreator?.titleRequired || "Titel ist erforderlich";
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await saveAdminLesson(moduleId, form, isNew);

      setSuccess(
        isNew
          ? t.contentCreator?.lessonCreated || "Lektion erfolgreich erstellt"
          : t.contentCreator?.lessonSaved || "Lektion erfolgreich gespeichert"
      );

      setTimeout(() => {
        navigate("/teacher/content");
      }, 1500);
    } catch (err) {
      console.error("Save error:", err);
      setError(t.contentCreator?.saveError || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (validation[field]) {
      setValidation((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Learning Objectives
  const addObjective = () => {
    if (newObjective.trim()) {
      setForm((prev) => ({
        ...prev,
        learning_objectives: [...prev.learning_objectives, newObjective.trim()]
      }));
      setNewObjective("");
    }
  };

  const removeObjective = (index) => {
    setForm((prev) => ({
      ...prev,
      learning_objectives: prev.learning_objectives.filter((_, i) => i !== index)
    }));
  };

  // Content Sections
  const addSection = () => {
    const newSection = {
      id: `${form.id || "L"}_S${form.content_sections.length + 1}`,
      type: "markdown",
      title: "",
      markdown: ""
    };
    setForm((prev) => ({
      ...prev,
      content_sections: [...prev.content_sections, newSection]
    }));
  };

  const updateSection = (index, updatedSection) => {
    setForm((prev) => ({
      ...prev,
      content_sections: prev.content_sections.map((s, i) =>
        i === index ? updatedSection : s
      )
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...form.content_sections];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setForm((prev) => ({ ...prev, content_sections: newSections }));
  };

  const deleteSection = (index) => {
    setForm((prev) => ({
      ...prev,
      content_sections: prev.content_sections.filter((_, i) => i !== index)
    }));
  };

  const generateLessonId = () => {
    const lessonNum = form.order_index || 1;
    const newId = `${moduleId}_L${lessonNum}_lesson`;
    handleChange("id", newId);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">{t.common?.loading || "Lädt..."}</p>
      </Container>
    );
  }

  return (
    <main
      className="lesson-creator-main"
      style={{
        backgroundColor: "var(--bg-secondary)",
        minHeight: "100vh"
      }}
    >
      <Container
        className="py-4"
        style={{ maxWidth: "900px" }}
      >
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <Button
            variant="link"
            className="p-0 me-3 text-secondary"
            onClick={() => navigate("/teacher/content")}
          >
            <ArrowLeft size={24} />
          </Button>
          <div>
            <h1 className="mb-0 h3">
              <FileText size={24} className="me-2 text-primary" />
              {isNew
                ? t.contentCreator?.createLesson || "Lektion erstellen"
                : t.contentCreator?.editLesson || "Lektion bearbeiten"}
            </h1>
            <Badge bg="secondary" className="mt-1">{moduleId}</Badge>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Basic Info Card */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">{t.contentCreator?.basicInfo || "Grundinformationen"}</h5>
            </Card.Header>
            <Card.Body>
              {/* Lesson ID */}
              <Form.Group className="mb-3">
                <Form.Label>{t.contentCreator?.lessonId || "Lektions-ID"} *</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={form.id}
                    onChange={(e) => handleChange("id", e.target.value)}
                    placeholder="z.B. M1_L1_grundlagen"
                    isInvalid={!!validation.id}
                    disabled={!isNew}
                  />
                  {isNew && (
                    <Button variant="outline-secondary" onClick={generateLessonId} type="button">
                      {t.contentCreator?.generate || "Generieren"}
                    </Button>
                  )}
                </div>
                <Form.Control.Feedback type="invalid">
                  {validation.id}
                </Form.Control.Feedback>
              </Form.Group>

              {/* Title */}
              <Form.Group className="mb-3">
                <Form.Label>{t.contentCreator?.lessonTitle || "Titel"} *</Form.Label>
                <Form.Control
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="z.B. Lektion 1: Grundrechnungsarten"
                  isInvalid={!!validation.title}
                />
                <Form.Control.Feedback type="invalid">
                  {validation.title}
                </Form.Control.Feedback>
              </Form.Group>

              {/* Description */}
              <Form.Group className="mb-3">
                <Form.Label>{t.contentCreator?.description || "Beschreibung"}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={t.contentCreator?.lessonDescPlaceholder || "Kurze Beschreibung der Lektion..."}
                />
              </Form.Group>

              {/* Metadata Row */}
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t.contentCreator?.category || "Kategorie"}</Form.Label>
                    <Form.Select
                      value={form.category}
                      onChange={(e) => handleChange("category", e.target.value)}
                    >
                      <option value="grundlagen">Grundlagen</option>
                      <option value="rechnen">Rechnen</option>
                      <option value="buchhaltung">Buchhaltung</option>
                      <option value="wirtschaft">Wirtschaft</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t.contentCreator?.difficulty || "Schwierigkeit"}</Form.Label>
                    <Form.Select
                      value={form.difficulty}
                      onChange={(e) => handleChange("difficulty", e.target.value)}
                    >
                      <option value="beginner">Anfänger</option>
                      <option value="intermediate">Fortgeschritten</option>
                      <option value="advanced">Experte</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t.contentCreator?.duration || "Dauer (min)"}</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={form.duration_minutes}
                      onChange={(e) => handleChange("duration_minutes", parseInt(e.target.value) || 30)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t.contentCreator?.points || "Punkte"}</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={form.points_reward}
                      onChange={(e) => handleChange("points_reward", parseInt(e.target.value) || 100)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t.contentCreator?.order || "Reihenfolge"}</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={form.order_index}
                      onChange={(e) => handleChange("order_index", parseInt(e.target.value) || 1)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Learning Objectives Card */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <Target size={18} className="me-2" />
                {t.contentCreator?.learningObjectives || "Lernziele"}
              </h5>
              <Badge bg="secondary">{form.learning_objectives.length}</Badge>
            </Card.Header>
            <Card.Body>
              {form.learning_objectives.map((obj, idx) => (
                <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge bg-light text-dark">{idx + 1}</span>
                  <span className="flex-grow-1">{obj}</span>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeObjective(idx)}
                  >
                    <X size={14} />
                  </Button>
                </div>
              ))}

              <div className="d-flex gap-2 mt-3">
                <Form.Control
                  type="text"
                  value={newObjective}
                  onChange={(e) => setNewObjective(e.target.value)}
                  placeholder={t.contentCreator?.newObjectivePlaceholder || "Neues Lernziel eingeben..."}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addObjective();
                    }
                  }}
                />
                <Button variant="outline-primary" onClick={addObjective} type="button">
                  <Plus size={16} />
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Content Sections Card */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <BookOpen size={18} className="me-2" />
                {t.contentCreator?.contentSections || "Inhaltsabschnitte"}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="secondary">{form.content_sections.length}</Badge>
                <Button variant="primary" size="sm" onClick={addSection} type="button">
                  <Plus size={14} className="me-1" />
                  {t.contentCreator?.addSection || "Abschnitt"}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {form.content_sections.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <BookOpen size={32} className="mb-2" />
                  <p className="mb-0">
                    {t.contentCreator?.noSections || "Noch keine Abschnitte. Klicken Sie oben auf 'Abschnitt' um zu beginnen."}
                  </p>
                </div>
              ) : (
                form.content_sections.map((section, idx) => (
                  <ContentSectionEditor
                    key={section.id || idx}
                    section={section}
                    index={idx}
                    totalSections={form.content_sections.length}
                    onChange={(updated) => updateSection(idx, updated)}
                    onMoveUp={() => moveSection(idx, -1)}
                    onMoveDown={() => moveSection(idx, 1)}
                    onDelete={() => deleteSection(idx)}
                    t={t}
                  />
                ))
              )}
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/teacher/content")}
              disabled={saving}
            >
              <ArrowLeft size={16} className="me-1" />
              {t.common?.back || "Zurück"}
            </Button>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                onClick={() => navigate(`/teacher/content/quiz/${moduleId}/${form.id || lessonId}`)}
                disabled={saving || isNew}
                type="button"
              >
                <BookOpen size={16} className="me-1" />
                {t.contentCreator?.editQuiz || "Quiz bearbeiten"}
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    {t.common?.loading || "Lädt..."}
                  </>
                ) : (
                  <>
                    <Save size={16} className="me-1" />
                    {t.common?.save || "Speichern"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Form>
      </Container>
    </main>
  );
}
